import { FC } from "react";

import importSuccess from "../../../assets/fonts/importSuccess.svg";
import { Notification } from "../../../types/global/types";
import { useTranslation } from "../../../hooks/useTranslation";
import "./styles.scss";

type Props = {
  notification: Notification;
};

export const RouteDivisionCompletedNotification: FC<Props> = ({ notification }) => {
  const { p } = useTranslation("notifications");
  const data = notification.notes as any;

  // Extract data from notes object
  let message: string | null =
    typeof data === "object" && data !== null
      ? data.message ?? null
      : null;

  // Remove duplicate "could not be optimized" text from message if it exists
  if (message && message.includes("could not be optimized")) {
    message = message.replace(/, \d+ could not be optimized/g, "");
  }

  const createdRoutes: number =
    typeof data === "object" && data !== null
      ? data.created_routes ?? 0
      : 0;

  const truncatedCount: number =
    typeof data === "object" && data !== null
      ? data.truncated_count ?? 0
      : 0;

  const skippedCount: number =
    typeof data === "object" && data !== null
      ? data.skipped_count ?? 0
      : 0;

  const optimizedRoutes: number =
    typeof data === "object" && data !== null
      ? data.optimized_routes ?? 0
      : 0;

  const unoptimizedRoutes: number =
    typeof data === "object" && data !== null
      ? data.unoptimized_routes ?? 0
      : 0;

  const hasAnyActivity = createdRoutes > 0 || truncatedCount > 0 || skippedCount > 0;

  return (
    <div className="notification">
      {/* Header: icon + title + timestamp */}
      <div className="notification__header">
        <div className="notification__reaction">
          <img src={importSuccess} alt="success" />
        </div>
        <div className="notification__title" style={{ width: "-webkit-fill-available" }}>
          <h3>{p("route_division_completed")}</h3>
          <div className="notification__time">
            {notification.created_at_formatted}
          </div>
        </div>
      </div>

      {/* Stats row: Routes created, excluded, skipped, optimized, unoptimized */}
      {hasAnyActivity && (
        <div className="notification__notes">
          <div className="notification__notes-header">
            {p("routes_created", { count: createdRoutes })}
            {truncatedCount > 0 && (
              <span>
                {" "}&bull;{"  "}{p("excluded", { count: truncatedCount })}
              </span>
            )}
            {skippedCount > 0 && (
              <span>
                {" "}&bull;{"  "}{p("skipped", { count: skippedCount })}
              </span>
            )}
          </div>

          {/* Summary message */}
          {message && (
            <div className="notification__notes-body">{message}</div>
          )}
        </div>
      )}

      {/* Additional details about optimization */}
      {(optimizedRoutes > 0 || unoptimizedRoutes > 0) && (
        <div className="notification__notes">
          <div className="notification__notes-header">{p("optimization_details")}</div>
          <div className="notification__notes-body">
            {optimizedRoutes > 0 && (
              <div>{p("optimized_routes", { count: optimizedRoutes })}</div>
            )}
            {unoptimizedRoutes > 0 && (
              <div>{p("could_not_be_optimized", { count: unoptimizedRoutes })}</div>
            )}
          </div>
        </div>
      )}

      {/* Edge case: nothing at all */}
      {!hasAnyActivity && (
        <div className="notification__info">{p("no_routes_created")}</div>
      )}
    </div>
  );
};
